import { Trash } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';

import { Button } from 'theSource/components/Button';
import { addSnackbar } from 'theSource/components/Snackbar';
import { DialogTitleStyled, RemoveButtonStyled } from './RemoveButtonStyled';
import { Dialog } from 'theSource/components/Dialog';
import { Spacer } from 'theSource/components/Spacer';

interface RemoveButtonProps {
  itemName: string;
  description: string;
  onConfirm: () => void;
  isLoading: boolean;
}

export const RemoveButton = ({
  itemName,
  description,
  onConfirm,
  isLoading,
}: RemoveButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation('messages.removeButton');

  const dispatch = useDispatch();

  const handleOpen = () => setIsOpen(true);

  const handleClose = () => setIsOpen(false);

  const handleConfirm = () => {
    onConfirm();
    handleClose();
    dispatch(
      addSnackbar({
        message: t('common:removeSuccessful'),
        severity: 'success',
      })
    );
  };

  return (
    <Box>
      <RemoveButtonStyled
        data-testid="remove-button"
        startIcon={<Trash size={18} weight="bold" />}
        variant="tertiary"
        label={t('removeItem', { itemName })}
        onClick={handleOpen}
        isLoading={isLoading}
      />
      <Dialog
        title={
          <DialogTitleStyled>
            {t('dialogTitle', { itemName })}
          </DialogTitleStyled>
        }
        message={description}
        isOpen={isOpen}
        testId="remove-button-dialog"
      >
        <Button
          data-testid="cancel-button"
          variant="tertiary"
          label={t('common:cancel')}
          onClick={handleClose}
        />
        <Spacer width={6} />
        <RemoveButtonStyled
          data-testid="confirm-button"
          variant="tertiary"
          label={t('common:remove')}
          onClick={handleConfirm}
          isLoading={isLoading}
        />
      </Dialog>
    </Box>
  );
};
